import { action, observable, set } from 'mobx';
import { HttpClient, IAccessTokenStore, ReviveDateTime, setMap } from 'Mri.Web.Common';

export interface IConfig {
    method?: string;
    data?: string | {};
    headers?: Headers;
}

export interface IFileSystemMetaConfiguration {
    publicChangelog?: string;
    internalChangelog?: string;
}

export interface IImportModel {
    files: FileList;
}

export class FileSystemsStore {
    public constructor(
        httpClient: HttpClient,
        accessTokenStore: IAccessTokenStore,
        fileSystemsStore?: FileSystemsStore
    ) {
        if (fileSystemsStore) set(this, fileSystemsStore);
        this.httpClient = httpClient;
        this.accessTokenStore = accessTokenStore;
    }

    @action
    public async uploadFile(model: IImportModel) {
        for (const file of model.files) {
            const headers = new Headers();
            headers.append('Accept', 'application/json');
            headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
            const formData = new FormData();
            formData.append('file', file);
            const response: Response = await fetch(`/api/v1/file-systems/upload`, {
                body: formData,
                cache: 'default',
                credentials: 'include',
                headers,
                method: 'POST',
                mode: 'cors',
            });
            const text = await response.text();
            const data = JSON.parse(text, ReviveDateTime);

            if (response.ok) {
                const fileSystem = data as Mri.FileSystems.FileSystem;
                this.fileSystems.set(fileSystem.fileSystemGuid, fileSystem);
            } else if (response.status == 400) {
                throw new Error(data);
            } else {
                throw data;
            }
        }
    }

    @action
    public async uploadAttachment(guid: string, model: IImportModel) {
        for (const file of model.files) {
            const headers = new Headers();
            headers.append('Accept', 'application/json');
            headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
            const formData = new FormData();
            formData.append('file', file);
            const response: Response = await fetch(`/api/v1/file-systems/${guid}/attachments/upload`, {
                body: formData,
                cache: 'default',
                credentials: 'include',
                headers,
                method: 'POST',
                mode: 'cors',
            });
            const text = await response.text();
            const data = JSON.parse(text, ReviveDateTime);

            if (response.ok) {
                const fileSystem = data as Mri.FileSystems.FileSystem;
                this.fileSystems.set(fileSystem.fileSystemGuid, fileSystem);
            } else if (response.status == 400) {
                throw new Error(data);
            } else {
                throw data;
            }
        }
    }

    @action
    public async loadAllAsync(topLevelOnly: boolean = false) {
        const fileSystems = await this.httpClient.get<Mri.FileSystems.FileSystem[]>(
            `/api/v1/file-systems?topLevelOnly=${topLevelOnly}`
        );

        setMap(this.fileSystems, fileSystems, d => d.fileSystemGuid);
    }

    @action
    public async deleteFileSystemFromStorageAsync(fileSystemGuid: string) {
        await this.httpClient.delete(`/api/v1/file-systems/${fileSystemGuid}/delete-from-storage`);

        this.fileSystems.delete(fileSystemGuid);
    }

    @action
    public getManifestNamesForFileSystem(fileSystemGuid: string): Promise<string[] | undefined> {
        return this.httpClient.get<string[]>(`/api/v1/file-systems/${fileSystemGuid}/get-associated-manifest-names`);
    }

    @action
    public async loadMetaAsync() {
        const fileSystemMeta = await this.httpClient.get<{ [id: string]: IFileSystemMetaConfiguration }>(
            `/api/v1/file-systems/meta`,
            {
                cache: true,
            }
        );

        if (fileSystemMeta) {
            this.fileSystemMeta = new Map<string, IFileSystemMetaConfiguration>(Object.entries(fileSystemMeta));
        }
    }

    @action
    public async loadAsync(guid: string) {
        const fileSystem = await this.httpClient.get<Mri.FileSystems.FileSystem>(`/api/v1/file-systems/${guid}`);

        if (fileSystem) this.fileSystems.set(fileSystem.fileSystemGuid, fileSystem);
    }

    @action
    public async updateAsync(guid: string, model: Mri.FileSystems.FileSystem) {
        const fileSystem = await this.httpClient.put<Mri.FileSystems.FileSystem>(`/api/v1/file-systems/${guid}`, model);

        if (fileSystem) this.fileSystems.set(fileSystem.fileSystemGuid, fileSystem);
    }

    @action
    public async deleteAsync(guid: string) {
        await this.httpClient.delete<Mri.FileSystems.FileSystem>(`/api/v1/file-systems/${guid}`);

        this.fileSystems.delete(guid);
    }

    @action
    public async deleteAttachmentAsync(guid: string, fileName: string) {
        const fileSystem = await this.httpClient.delete<Mri.FileSystems.FileSystem>(
            `/api/v1/file-systems/${guid}/attachments/${fileName}`
        );

        if (fileSystem) this.fileSystems.set(fileSystem.fileSystemGuid, fileSystem);
    }

    @action
    public async downloadAllAsync(guid: string) {
        const headers = new Headers();
        headers.append('Accept', 'application/zip');
        headers.append('Authorization', 'Bearer ' + (await this.accessTokenStore.getAccessToken()));
        const response: Response = await fetch(`/api/v1/file-systems/${guid}/zip`, {
            cache: 'default',
            credentials: 'include',
            headers,
            method: 'GET',
            mode: 'cors',
        });

        if (!response.ok) {
            throw new Error('Failed to export');
        }

        const blob = await response.blob();
        saveAs(blob, 'dependencies.zip');
    }

    @observable public fileSystems = new Map<string, Mri.FileSystems.FileSystem>();
    @observable public fileSystemMeta = new Map<string, IFileSystemMetaConfiguration>();

    private readonly httpClient: HttpClient;
    private readonly accessTokenStore: IAccessTokenStore;
}
